<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { NModal } from 'naive-ui'
import wheatLeft from '@/assets/pk/pk-1.png'
import wheatRight from '@/assets/pk/pk-2.png'

/** 海报主题；red-gold 用于 PK/冲刺，white-gold 用于基础目标达成。 */
type CelebrationPosterTheme = 'red-gold' | 'white-gold'

const props = withDefaults(
  defineProps<{
    show: boolean
    theme: CelebrationPosterTheme
    backgroundUrl: string
    slogan: string
    headline: string
    stageTitle: string
    subjectName: string
    metricLabel?: string
    displayValue?: string
    primaryText: string
    duration?: number
    hasSound?: boolean
  }>(),
  { duration: 10, hasSound: false, metricLabel: '', displayValue: '' },
)
const emit = defineEmits<{ close: []; primary: [] }>()

interface ConfettiPiece {
  x: number
  y: number
  vx: number
  vy: number
  rotation: number
  rotationVelocity: number
  width: number
  height: number
  color: string
  alpha: number
  type: 'ribbon' | 'rect'
}

interface FireworkSpark {
  x: number
  y: number
  vx: number
  vy: number
  life: number
  color: string
}

const stageRef = ref<HTMLElement | null>(null)
const canvasRef = ref<HTMLCanvasElement | null>(null)

/** 根据展示金额文本长度自适应字号，确保在框撑开时数字完整、大方且绝不截断。 */
const metricFontSize = computed(() => {
  const length = String(props.displayValue || '').trim().length
  if (length >= 14) return '52px'
  if (length >= 11) return '62px'
  if (length >= 8) return '72px'
  return '82px'
})
const reducedMotion = ref(false)
const palette = ['#ffe59a', '#fff8d4', '#ffca42', '#ff6b55', '#ff3852', '#ffd6e0', '#ffffff']
let countdownTimer: ReturnType<typeof setInterval> | undefined
let animationFrame = 0
let resizeObserver: ResizeObserver | undefined
let motionQuery: MediaQueryList | undefined
let canvasContext: CanvasRenderingContext2D | null = null
let stageWidth = 0
let stageHeight = 0
let pixelRatio = 1
let previousFrameTime = 0
let confetti: ConfettiPiece[] = []
let fireworks: FireworkSpark[][] = []
let audioContext: AudioContext | undefined

/** 生成单片彩纸或彩带；initial 决定首次是否分散覆盖整个舞台。 */
function createConfetti(initial: boolean): ConfettiPiece {
  return {
    x: Math.random() * stageWidth,
    y: initial ? Math.random() * stageHeight : -30,
    vx: (Math.random() - 0.5) * 1.8,
    vy: 1 + Math.random() * 2.8,
    rotation: Math.random() * Math.PI * 2,
    rotationVelocity: (Math.random() - 0.5) * 0.18,
    width: 4 + Math.random() * 9,
    height: 5 + Math.random() * 17,
    color: palette[Math.floor(Math.random() * palette.length)],
    alpha: 0.55 + Math.random() * 0.45,
    type: Math.random() > 0.6 ? 'ribbon' : 'rect',
  }
}

/** 在指定舞台位置生成一次随机礼花爆点。 */
function createFirework(x: number, y: number) {
  const sparks: FireworkSpark[] = []
  for (let index = 0; index < 38; index += 1) {
    const angle = Math.random() * Math.PI * 2
    const speed = 1 + Math.random() * 3.3
    sparks.push({
      x,
      y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      life: 1,
      color: palette[Math.floor(Math.random() * palette.length)],
    })
  }
  fireworks.push(sparks)
}

/** 根据容器尺寸和设备像素比重建 Canvas；参数由 ResizeObserver 提供，无业务入参。 */
function resizeCanvas() {
  const stage = stageRef.value
  const canvas = canvasRef.value
  if (!stage || !canvas) return
  const rect = stage.getBoundingClientRect()
  stageWidth = rect.width
  stageHeight = rect.height
  pixelRatio = Math.min(window.devicePixelRatio || 1, 2)
  canvas.width = Math.round(stageWidth * pixelRatio)
  canvas.height = Math.round(stageHeight * pixelRatio)
  canvasContext = canvas.getContext('2d')
  canvasContext?.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0)
  confetti = Array.from({ length: 150 }, () => createConfetti(true))
  fireworks = []
}

/** 绘制一帧彩纸和随机礼花；frameTime 为 requestAnimationFrame 的高精度时间戳。 */
function drawCanvas(frameTime: number) {
  if (!canvasContext || reducedMotion.value || !props.show) return
  const delta = Math.min(34, frameTime - previousFrameTime || 16)
  previousFrameTime = frameTime
  canvasContext.clearRect(0, 0, stageWidth, stageHeight)
  confetti.forEach((piece, index) => {
    piece.x += (piece.vx * delta) / 16
    piece.y += (piece.vy * delta) / 16
    piece.rotation += (piece.rotationVelocity * delta) / 16
    if (piece.y > stageHeight + 30) {
      confetti[index] = createConfetti(false)
      return
    }
    canvasContext?.save()
    canvasContext?.translate(piece.x, piece.y)
    canvasContext?.rotate(piece.rotation)
    if (canvasContext) {
      canvasContext.globalAlpha = piece.alpha
      canvasContext.fillStyle = piece.color
      canvasContext.shadowColor = piece.color
      canvasContext.shadowBlur = 5
      if (piece.type === 'ribbon') {
        canvasContext.beginPath()
        canvasContext.moveTo(-piece.width, 0)
        canvasContext.quadraticCurveTo(0, -piece.height, piece.width, 0)
        canvasContext.quadraticCurveTo(0, piece.height, -piece.width, 0)
        canvasContext.fill()
      } else {
        canvasContext.fillRect(-piece.width / 2, -piece.height / 2, piece.width, piece.height)
      }
    }
    canvasContext?.restore()
  })
  if (Math.random() < 0.022) {
    createFirework(
      stageWidth * (0.12 + Math.random() * 0.76),
      stageHeight * (0.06 + Math.random() * 0.37),
    )
  }
  fireworks = fireworks.filter((sparks) => sparks.some((spark) => spark.life > 0))
  fireworks.forEach((sparks) => {
    sparks.forEach((spark) => {
      spark.x += spark.vx
      spark.y += spark.vy
      spark.vy += 0.024
      spark.life -= 0.018
      if (!canvasContext) return
      canvasContext.save()
      canvasContext.globalAlpha = Math.max(0, spark.life)
      canvasContext.fillStyle = spark.color
      canvasContext.shadowColor = spark.color
      canvasContext.shadowBlur = 11
      canvasContext.beginPath()
      canvasContext.arc(spark.x, spark.y, 2.2, 0, Math.PI * 2)
      canvasContext.fill()
      canvasContext.restore()
    })
  })
  animationFrame = window.requestAnimationFrame(drawCanvas)
}

/** 释放 Canvas 的动画帧与尺寸观察器；没有参数，组件关闭和卸载时复用。 */
function stopCanvas() {
  if (animationFrame) window.cancelAnimationFrame(animationFrame)
  animationFrame = 0
  resizeObserver?.disconnect()
  resizeObserver = undefined
  canvasContext?.clearRect(0, 0, stageWidth, stageHeight)
  canvasContext = null
}

/** 在非降动效模式启动 Canvas；无参数，依赖已挂载的海报元素。 */
async function startCanvas() {
  stopCanvas()
  if (!props.show || reducedMotion.value) return
  await nextTick()
  if (!stageRef.value || !canvasRef.value) return
  resizeObserver = new ResizeObserver(resizeCanvas)
  resizeObserver.observe(stageRef.value)
  resizeCanvas()
  previousFrameTime = 0
  animationFrame = window.requestAnimationFrame(drawCanvas)
}

/** 停止自动关闭计时器；无参数，避免关闭后遗留异步回调。 */
function stopCountdown() {
  if (countdownTimer) clearInterval(countdownTimer)
  countdownTimer = undefined
}

/** 启动本次海报的自动关闭计时；固定使用后端/调用方提供的秒数。 */
function startCountdown() {
  stopCountdown()
  const duration = Math.max(1, Number(props.duration) || 10)
  let remaining = duration
  countdownTimer = setInterval(() => {
    remaining -= 1
    if (remaining <= 0) handleClose()
  }, 1000)
}

/** 播放 PK 的轻量提示音；浏览器拒绝自动播放时静默降级。 */
function playSound() {
  if (!props.hasSound) return
  try {
    void audioContext?.close().catch(() => undefined)
    const context = new AudioContext()
    const oscillator = context.createOscillator()
    const gain = context.createGain()
    oscillator.connect(gain)
    gain.connect(context.destination)
    gain.gain.setValueAtTime(0.03, context.currentTime)
    gain.gain.exponentialRampToValueAtTime(0.001, context.currentTime + 0.5)
    oscillator.frequency.setValueAtTime(660, context.currentTime)
    oscillator.start()
    oscillator.stop(context.currentTime + 0.5)
    audioContext = context
    void context.resume().catch(() => undefined)
  } catch {
    /* 提示音不能影响庆祝海报本身。 */
  }
}

/** 通知宿主结束本次海报，宿主负责 ACK 与继续领取。 */
function handleClose() {
  stopCountdown()
  emit('close')
}

/**
 * 接收 NModal 的显隐请求；仅在请求关闭时通知宿主更新受控展示状态。
 * @param visible NModal 请求变更后的展示状态
 */
function handleModalShowUpdate(visible: boolean) {
  if (!visible) handleClose()
}

/** 通知宿主执行主按钮动作；宿主先 ACK 后完成路由跳转。 */
function handlePrimary() {
  stopCountdown()
  emit('primary')
}

/** 响应系统降动效偏好变化；event 为 MediaQueryList 的变更结果。 */
function handleMotionChange(event: MediaQueryListEvent) {
  reducedMotion.value = event.matches
  if (event.matches) stopCanvas()
  else void startCanvas()
}

watch(
  () => props.show,
  (visible) => {
    stopCountdown()
    if (!visible) {
      stopCanvas()
      return
    }
    startCountdown()
    playSound()
    void startCanvas()
  },
  { immediate: true },
)

onMounted(() => {
  motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
  reducedMotion.value = motionQuery.matches
  motionQuery.addEventListener('change', handleMotionChange)
  void startCanvas()
})
onBeforeUnmount(() => {
  stopCountdown()
  stopCanvas()
  motionQuery?.removeEventListener('change', handleMotionChange)
  void audioContext?.close().catch(() => undefined)
})
</script>

<template>
  <NModal
    :show="show"
    :mask-closable="false"
    :close-on-esc="true"
    :block-scroll="true"
    transform-origin="center"
    class="celebration-poster-modal"
    @update:show="handleModalShowUpdate"
  >
    <section
      ref="stageRef"
      class="celebration-poster"
      :class="`celebration-poster--${theme}`"
      :style="{ '--celebration-background': `url('${backgroundUrl}')` }"
      aria-live="assertive"
    >
      <div class="celebration-poster__breath" />
      <div class="celebration-poster__spotlight celebration-poster__spotlight--left" />
      <div class="celebration-poster__spotlight celebration-poster__spotlight--right" />
      <canvas ref="canvasRef" class="celebration-poster__canvas" aria-hidden="true" />
      <div class="celebration-poster__vignette" aria-hidden="true" />

      <!-- 舞台锚定层：以 1920x958 画布为基准，严格跟随背景图 cover 缩放比例居中贴合 -->
      <div
        class="celebration-poster__stage"
        :style="{
          '--metric-font-size': metricFontSize,
        }"
      >
        <div class="celebration-poster__halo" />
        <!-- <div class="celebration-poster__ring" /> -->
        <div class="celebration-poster__content">
          <p class="celebration-poster__slogan">{{ slogan }}</p>
          <h1 class="celebration-poster__headline">{{ headline }}</h1>
          <div class="celebration-poster__stage-line">
            <span />
            <strong>{{ stageTitle }}</strong>
            <span />
          </div>
          <p class="celebration-poster__subject">{{ subjectName }}</p>
          <section v-if="displayValue" class="celebration-poster__value-card">
            <img
              :src="wheatLeft"
              alt=""
              class="celebration-poster__wheat celebration-poster__wheat--left"
            />
            <strong class="celebration-poster__metric-value">{{ displayValue }}</strong>
            <img
              :src="wheatRight"
              alt=""
              class="celebration-poster__wheat celebration-poster__wheat--right"
            />
          </section>
          <button class="celebration-poster__primary" type="button" @click="handlePrimary">
            {{ primaryText }}
          </button>
        </div>
      </div>
      <button class="celebration-poster__close" type="button" @click="handleClose">
        关闭后继续当前操作
      </button>
    </section>
  </NModal>
</template>

<style scoped>
/* 预览大屏仅淡入淡出，避免 Naive UI 默认缩放整张背景海报。 */
:deep(.celebration-poster-modal.fade-in-scale-up-transition-enter-active),
:deep(.celebration-poster-modal.fade-in-scale-up-transition-leave-active) {
  transition: opacity 0.2s ease !important;
}

:deep(.celebration-poster-modal.fade-in-scale-up-transition-enter-from),
:deep(.celebration-poster-modal.fade-in-scale-up-transition-leave-to) {
  opacity: 0;
  transform: none !important;
}

:deep(.celebration-poster-modal.fade-in-scale-up-transition-enter-to),
:deep(.celebration-poster-modal.fade-in-scale-up-transition-leave-from) {
  opacity: 1;
  transform: none !important;
}

/* NModal 默认内容带有尺寸和居中约束；全屏庆祝海报必须占满窗口。 */
:deep(.celebration-poster-modal.n-modal) {
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100vh;
  max-width: none;
  margin: 0;
  transform: none !important;
}

.celebration-poster {
  width: 100vw;
  height: 100vh;
  --poster-ink: #fff4d1;
  --poster-muted: rgba(255, 244, 211, 0.78);
  --poster-line: rgba(255, 238, 142, 0.72);
  --poster-card: rgba(95, 3, 9, 0.54);
  --poster-card-border: rgba(255, 220, 125, 0.7);
  /* 背景和文案统一以窗口中心为基准，避免单独移动背景造成错位。 */
  --celebration-background-position: center;
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: grid;
  min-width: 320px;
  overflow: hidden;
  isolation: isolate;
  place-items: center;
  color: var(--poster-ink);
  background:
    linear-gradient(90deg, rgba(70, 0, 0, 0.24), transparent 23% 77%, rgba(70, 0, 0, 0.24)),
    var(--celebration-background) var(--celebration-background-position) / cover no-repeat,
    #8c0005;
  font-family: 'PingFang SC', 'Microsoft YaHei', sans-serif;
}
.celebration-poster--white-gold {
  --poster-ink: #6c3414;
  --poster-muted: rgba(107, 52, 20, 0.76);
  --poster-line: rgba(188, 129, 34, 0.7);
  --poster-card: rgba(255, 253, 242, 0.74);
  --poster-card-border: rgba(211, 153, 59, 0.68);
  background:
    linear-gradient(90deg, rgba(255, 248, 218, 0.1), transparent 23% 77%, rgba(255, 248, 218, 0.1)),
    var(--celebration-background) var(--celebration-background-position) / cover no-repeat,
    #f6e7bf;
}
.celebration-poster::after {
  position: absolute;
  z-index: 3;
  inset: -25%;
  pointer-events: none;
  content: '';
  background: linear-gradient(
    112deg,
    transparent 42%,
    rgba(255, 246, 170, 0.26) 48%,
    rgba(255, 255, 255, 0.16) 50%,
    transparent 56%
  );
  mix-blend-mode: screen;
  transform: translateX(-75%);
  animation: celebration-sweep 7s ease-in-out infinite;
}
.celebration-poster__breath {
  position: absolute;
  z-index: 1;
  inset: 0;
  pointer-events: none;
  background:
    radial-gradient(ellipse at 50% 24%, rgba(255, 241, 158, 0.22), transparent 27%),
    linear-gradient(90deg, rgba(70, 0, 0, 0.18), transparent 23% 77%, rgba(70, 0, 0, 0.18));
  mix-blend-mode: screen;
  animation: celebration-breath 4.5s ease-in-out infinite;
}
.celebration-poster__spotlight {
  position: absolute;
  top: -12%;
  z-index: 2;
  width: 28%;
  height: 125%;
  opacity: 0.15;
  pointer-events: none;
  background: linear-gradient(90deg, transparent, rgba(255, 238, 153, 0.85), transparent);
  filter: blur(8px);
  mix-blend-mode: screen;
}
.celebration-poster__spotlight--left {
  left: 5%;
  transform: rotate(19deg);
  animation: celebration-spotlight-left 6s ease-in-out infinite;
}
.celebration-poster__spotlight--right {
  right: 5%;
  transform: rotate(-19deg);
  animation: celebration-spotlight-right 6s ease-in-out 0.8s infinite;
}
.celebration-poster__canvas {
  position: absolute;
  z-index: 4;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}
.celebration-poster__vignette {
  position: absolute;
  z-index: 6;
  inset: 0;
  pointer-events: none;
  box-shadow: inset 0 0 120px rgba(35, 0, 2, 0.64);
}
.celebration-poster--white-gold .celebration-poster__vignette {
  box-shadow: inset 0 0 120px rgba(117, 73, 15, 0.2);
}
/* 文案层使用全屏响应式坐标，不再通过固定 1920x958 舞台整体缩小文字。 */
.celebration-poster__stage {
  position: absolute;
  z-index: 7;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}
.celebration-poster__halo,
.celebration-poster__ring {
  position: absolute;
  top: 20px;
  left: 50%;
  z-index: 2;
  width: 680px;
  aspect-ratio: 1;
  pointer-events: none;
}
.celebration-poster__halo {
  width: 520px;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(255, 244, 158, 0.36) 0 3%,
    rgba(255, 212, 60, 0.18) 17%,
    transparent 63%
  );
  filter: blur(9px);
  transform: translateX(-50%);
  animation: celebration-halo 3.8s ease-in-out infinite;
}
.celebration-poster__ring {
  border: 2px solid rgba(255, 238, 142, 0.3);
  border-radius: 50%;
  box-shadow:
    0 0 35px rgba(255, 191, 49, 0.3),
    inset 0 0 35px rgba(255, 191, 49, 0.12);
  transform: translateX(-50%);
  animation: celebration-ring 9s linear infinite;
}
.celebration-poster__ring::before,
.celebration-poster__ring::after {
  position: absolute;
  inset: 6%;
  border: 1px dashed rgba(255, 240, 164, 0.3);
  border-radius: 50%;
  content: '';
}
.celebration-poster__ring::after {
  inset: 16%;
  animation: celebration-ring-reverse 7s linear infinite;
}
.celebration-poster__content {
  position: absolute;
  inset: 0;
  text-align: center;
}
.celebration-poster__slogan,
.celebration-poster__headline,
.celebration-poster__stage-line,
.celebration-poster__subject,
.celebration-poster__value-card,
.celebration-poster__primary {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
}
.celebration-poster__slogan {
  top: 43.6%;
  width: max-content;
  max-width: calc(100vw - 64px);
  margin: 0;
  color: var(--poster-muted);
  font-size: clamp(18px, 1.72vw, 30px);
  font-weight: 500;
  letter-spacing: 0.48em;
  line-height: 1.45;
  text-indent: 0.48em;
  white-space: nowrap;
}
.celebration-poster__headline {
  top: 48.8%;
  max-width: calc(100vw - 64px);
  margin: 0;
  color: var(--poster-ink);
  font-size: clamp(36px, 4.375vw, 72px);
  font-weight: 800;
  letter-spacing: 0.05em;
  line-height: 1.28;
  text-align: center;
  text-shadow: 0 4px 10px rgba(79, 14, 0, 0.46);
  white-space: nowrap;
}
.celebration-poster__stage-line {
  top: 57.8%;
  display: flex;
  width: min(640px, calc(100vw - 96px));
  align-items: center;
  gap: clamp(10px, 1.25vw, 16px);
  color: var(--poster-line);
  line-height: 1.4;
}
.celebration-poster__stage-line span {
  height: 1px;
  flex: 1;
  background: linear-gradient(90deg, transparent, currentColor);
}
.celebration-poster__stage-line span:last-child {
  background: linear-gradient(90deg, currentColor, transparent);
}
.celebration-poster__stage-line strong {
  display: flex;
  flex: none;
  align-items: center;
  gap: clamp(8px, 0.94vw, 12px);
  font-size: clamp(20px, 1.875vw, 30px);
  font-weight: 600;
  letter-spacing: 0.16em;
  white-space: nowrap;
}
.celebration-poster__stage-line strong::before,
.celebration-poster__stage-line strong::after {
  color: var(--poster-line);
  content: '◆ ◆';
  font-size: 8px;
  letter-spacing: 1px;
}
.celebration-poster__subject {
  top: 62.8%;
  width: min(1200px, calc(100vw - 96px));
  max-width: calc(100vw - 64px);
  margin: 0;
  color: var(--poster-muted);
  font-size: clamp(16px, 1.25vw, 24px);
  letter-spacing: 0.08em;
  line-height: 1.5;
  white-space: nowrap;
}
.celebration-poster__value-card {
  top: 67.6%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: max-content;
  min-width: clamp(420px, 42vw, 760px);
  max-width: calc(100vw - 64px);
  height: clamp(110px, 11.4vh, 146px);
  padding: 0 clamp(24px, 2.3vw, 44px);
  column-gap: clamp(16px, 1.46vw, 28px);
  border: 1px solid var(--poster-card-border);
  border-radius: 18px;
  background: var(--poster-card);
  box-shadow:
    0 12px 40px rgba(64, 13, 0, 0.24),
    inset 0 1px rgba(255, 255, 255, 0.25);
  backdrop-filter: blur(5px);
  pointer-events: auto;
}
.celebration-poster__wheat {
  width: clamp(64px, 5vw, 96px);
  height: auto;
  flex-shrink: 0;
  object-fit: contain;
}
.celebration-poster__metric-value {
  display: inline-block;
  flex-shrink: 0;
  color: var(--poster-ink);
  font-size: var(--metric-font-size, 82px);
  font-variant-numeric: tabular-nums;
  font-weight: 800;
  letter-spacing: 0.035em;
  line-height: 1;
  text-align: center;
  text-shadow: 0 3px 10px rgba(71, 17, 0, 0.32);
  white-space: nowrap;
}
.celebration-poster__primary {
  top: 85.6%;
  box-sizing: border-box;
  width: clamp(260px, 18.23vw, 350px);
  height: clamp(56px, 5.3vw, 68px);
  padding: 0;
  border: 0;
  border-radius: 34px;
  color: #6c2108;
  background: linear-gradient(180deg, #fff6cf, #ffd980);
  box-shadow:
    0 9px 24px rgba(66, 12, 0, 0.28),
    inset 0 1px rgba(255, 255, 255, 0.86);
  cursor: pointer;
  font-size: clamp(20px, 1.46vw, 28px);
  font-weight: 700;
  letter-spacing: 0.04em;
  line-height: 1;
  pointer-events: auto;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
}
.celebration-poster__primary:hover {
  box-shadow:
    0 13px 30px rgba(66, 12, 0, 0.38),
    inset 0 1px rgba(255, 255, 255, 0.9);
  transform: translateX(-50%) translateY(-2px);
}
.celebration-poster__primary:focus-visible,
.celebration-poster__close:focus-visible {
  outline: 2px solid #fff;
  outline-offset: 4px;
}
.celebration-poster__close {
  position: absolute;
  z-index: 8;
  bottom: 14px;
  left: 50%;
  padding: 0;
  border: 0;
  color: var(--poster-muted);
  background: transparent;
  cursor: pointer;
  font-size: 18px;
  letter-spacing: 0.08em;
  line-height: 28px;
  transform: translateX(-50%);
}
.celebration-poster__close:hover {
  color: var(--poster-ink);
  text-decoration: underline;
  text-underline-offset: 4px;
}
@keyframes celebration-breath {
  0%,
  100% {
    opacity: 0.55;
  }
  50% {
    opacity: 1;
  }
}
@keyframes celebration-sweep {
  0%,
  40% {
    transform: translateX(-75%) rotate(0deg);
  }
  66%,
  100% {
    transform: translateX(75%) rotate(0deg);
  }
}
@keyframes celebration-spotlight-left {
  0%,
  100% {
    opacity: 0.08;
    transform: rotate(19deg) translateX(-10%);
  }
  50% {
    opacity: 0.32;
    transform: rotate(9deg) translateX(16%);
  }
}
@keyframes celebration-spotlight-right {
  0%,
  100% {
    opacity: 0.08;
    transform: rotate(-19deg) translateX(10%);
  }
  50% {
    opacity: 0.32;
    transform: rotate(-9deg) translateX(-16%);
  }
}
@keyframes celebration-halo {
  0%,
  100% {
    opacity: 0.65;
    transform: translateX(-50%) scale(0.88);
  }
  50% {
    opacity: 1;
    transform: translateX(-50%) scale(1.12);
  }
}
@keyframes celebration-ring {
  to {
    transform: translateX(-50%) rotate(360deg);
  }
}
@keyframes celebration-ring-reverse {
  to {
    transform: rotate(-360deg);
  }
}
@media (prefers-reduced-motion: reduce) {
  .celebration-poster *,
  .celebration-poster::after,
  .celebration-poster *::before,
  .celebration-poster *::after {
    animation: none !important;
    transition: none !important;
  }
}
</style>
